import { useEffect, useState } from 'react';
import axios from 'axios';
import { Filter, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useGeo } from '@/context/GeoContext';
import { getCountry, getCountryFlag, getCountryName } from '@/lib/i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Country filter for the Events page.
 *
 * UX:
 *   ┌──────────────────────────────┐
 *   │ ⛢ 🇬🇧 United Kingdom  ▾    │  ← icon + flag + name (compact trigger)
 *   └──────────────────────────────┘
 *
 * The dropdown is populated from `/api/events/countries`, so it only ever
 * lists countries that actually have events. "🌍 All countries" sits on top.
 *
 * The user's signup country is the default selection (driven by GeoContext).
 */
export const EventsCountryFilter = ({ testId = 'events-country-filter' }) => {
  const { scope, filterCountry, setCountry, country: defaultCountry } = useGeo();
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/events/countries`);
        if (cancelled) return;
        const rows = (data?.items || [])
          .map((r) => ({ ...r, country: getCountry(r.code) }))
          .filter((r) => r.country)
          .sort((a, b) => a.country.name.localeCompare(b.country.name));
        setItems(rows);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build a label for the trigger
  const isAll = scope === 'global';
  const activeCode = isAll ? null : (filterCountry || defaultCountry);
  const flag = isAll ? null : getCountryFlag(activeCode);
  const name = isAll ? 'All countries' : getCountryName(activeCode);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/40 hover:bg-card/70 hover:border-border transition px-4 py-2 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        data-testid={`${testId}-trigger`}
      >
        <Filter className="w-4 h-4 text-muted-foreground" />
        {isAll ? (
          <Globe className="w-5 h-5 text-muted-foreground" />
        ) : (
          <span className="text-lg leading-none" aria-hidden="true">{flag}</span>
        )}
        <span className="truncate max-w-[180px]">{name}</span>
        <span aria-hidden="true" className="text-muted-foreground text-xs">▾</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={{ top: 80, bottom: 96 }}
        avoidCollisions
        className="w-[240px] overflow-y-auto max-h-[var(--radix-dropdown-menu-content-available-height)]"
        data-testid={`${testId}-menu`}
      >
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Filter by country
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => setCountry('')}
          data-testid={`${testId}-all`}
          className="cursor-pointer"
        >
          <Globe className="w-4 h-4 mr-2 text-muted-foreground" />
          <span className="flex-1">All countries</span>
          {isAll && <span className="text-primary text-xs">✓</span>}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground text-center">
            No countries with events yet
          </div>
        ) : (
          items.map((r) => {
            const selected = !isAll && activeCode === r.code;
            return (
              <DropdownMenuItem
                key={r.code}
                onClick={() => setCountry(r.code)}
                data-testid={`${testId}-${r.code}`}
                className="cursor-pointer"
              >
                <span className="mr-2 text-base leading-none" aria-hidden="true">{r.country.flag}</span>
                <span className="flex-1">{r.country.name}</span>
                <span className="text-xs text-muted-foreground mr-2">{r.count}</span>
                {selected && <span className="text-primary text-xs">✓</span>}
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default EventsCountryFilter;
